import type { ReactNode } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { formatClubAwardTotal, type TClass, type TShow } from '@schemas'
import {
  useCurrentUser,
  useDivisions,
  useClubParticipationReport,
  useReportResults,
  useShowStats,
} from '@/api/hooks'
import { useShowResultsRelease } from '@/api/show-release'
import { useCurrentShow } from '@/context/current-show'
import { clubAwardPlaces, clubAwards, clubsWithStanding } from '@/components/club-awards'
import { SelectShowNotice } from '@/components/current-show'
import { Button } from '@/components/ui/button'
import { formatRoleList, useActorPermissions } from '@/hooks/useActorPermissions'
import { useShowSlug } from '@/hooks/useShowSlug'
import { showAware } from '@/lib/paths'
import { parseApiErrorStatus } from '@/lib/api-errors'
import { orderPlacementColumns } from '@/lib/report-results'
import { summarizeJudgingProgress } from '@/lib/run-section-status'
import '@/styles/show-day-board.css'

type ReadState = { data: unknown; isLoading: boolean; isError: boolean; error: Error | null }

/** Several reads are checked in order; the first one without data decides the message. */
function ReadPanel({ query, children }: { query: ReadState | ReadState[]; children: ReactNode }) {
  const missing = [query].flat().find(read => !read.data)
  if (missing) {
    if (missing.isError)
      return <p role="alert">This information is unavailable. Retrying automatically.</p>
    return <p role="status">Loading…</p>
  }
  return <>{children}</>
}

const STATE_LABEL = {
  done: 'Results in',
  active: 'Being judged now',
  waiting: 'Waiting',
  empty: 'No entries',
} as const
type ClassState = keyof typeof STATE_LABEL

/** A class with no entries sits outside judging progress, as it does on the Show Dashboard. */
function classState(cls: TClass, entriesCount?: number): ClassState {
  if (entriesCount === 0) return 'empty'
  if (cls.resultsFinalizedAt) return 'done'
  if (cls.judgingStartedAt || cls.hasPlacements) return 'active'
  return 'waiting'
}

/** The presentation route sits outside AppShell, so its notices need their own landmark and way back. */
function Notice({ presentation, children }: { presentation: boolean; children: ReactNode }) {
  const currentSlug = useShowSlug()
  if (!presentation) return <>{children}</>
  return (
    <main className="space-y-4 p-6">
      {children}
      <Link className="underline" to={showAware('/run', { currentSlug })}>
        Show Dashboard
      </Link>
    </main>
  )
}

export default function ShowDayBoardPage({ presentation = false }: { presentation?: boolean }) {
  const { currentShow } = useCurrentShow()
  const currentSlug = useShowSlug()
  const user = useCurrentUser()
  const permissions = useActorPermissions(currentShow?.id)
  if (!currentShow) {
    return (
      <Notice presentation={presentation}>
        <SelectShowNotice />
      </Notice>
    )
  }
  if (currentShow.labelOnlyMode) return <Navigate replace to={showAware('/run', { currentSlug })} />
  if (user.isLoading) {
    return (
      <Notice presentation={presentation}>
        <p role="status">Loading staff access…</p>
      </Notice>
    )
  }
  if (!permissions.can('reports:results')) {
    return (
      <Notice presentation={presentation}>
        <p role="alert">
          Viewing the show day board requires{' '}
          {formatRoleList(permissions.getAllowedRoles('reports:results'))}.
        </p>
      </Notice>
    )
  }
  return <Board key={currentShow.id} show={currentShow} presentation={presentation} />
}

function Board({ show, presentation }: { show: TShow; presentation: boolean }) {
  const currentSlug = useShowSlug()
  const divisions = useDivisions(show.id)
  const results = useReportResults(show.id)
  const clubs = useClubParticipationReport(show.enableClubs ? show.id : undefined, {
    includeUnknown: false,
  })
  const stats = useShowStats(show.id)
  const release = useShowResultsRelease(show.id!, { live: true })
  const reads = [divisions, results, stats, release, ...(show.enableClubs ? [clubs] : [])]
  const clubStanding = clubsWithStanding(clubs.data?.rows ?? [])
  if (
    reads.some(
      query => query.isError && [401, 403, 404].includes(parseApiErrorStatus(query.error) ?? 0)
    )
  ) {
    return (
      <Notice presentation={presentation}>
        <p role="alert">The show day board is unavailable. Check your staff access.</p>
      </Notice>
    )
  }
  const stale = reads.some(query => query.isError)
  const classes = [...(divisions.data ?? [])]
    .sort((a, b) => a.order - b.order)
    .flatMap(division =>
      [...division.classes].sort(
        (a, b) => a.order - b.order || a.code.localeCompare(b.code, undefined, { numeric: true })
      )
    )
  const resultClasses = new Map(
    results.data?.divisions.flatMap(division =>
      division.classes.map(cls => [cls.classId, cls] as const)
    )
  )
  // Class states need the entry counts from the results read as well as the class list.
  const classReads = [divisions, results]
  const stateOf = (cls: TClass) =>
    classState(cls, cls.id ? resultClasses.get(cls.id)?.entriesCount : undefined)
  const entered = classes.filter(cls => stateOf(cls) !== 'empty')
  const progress = summarizeJudgingProgress(divisions.data, {
    includedClassIds: new Set(entered.map(cls => cls.id ?? cls.code)),
  })
  const active = entered.filter(cls => stateOf(cls) === 'active')
  const latest = entered
    .filter(cls => cls.resultsFinalizedAt)
    .sort((a, b) => Date.parse(b.resultsFinalizedAt!) - Date.parse(a.resultsFinalizedAt!))
    .slice(0, 6)
  const boardPath = showAware('/run/board', { currentSlug })
  const Container = presentation ? 'main' : 'div'
  return (
    <Container className={`show-day-board ${presentation ? 'show-day-board-presentation' : ''}`}>
      <header className="board-header">
        <div>
          <p className="board-eyebrow">Signed-in staff only · Exhibit IDs only</p>
          <h1>Show day board</h1>
          <p>{show.name}</p>
        </div>
        <div className="board-controls">
          <Button asChild variant="outline">
            <Link to={presentation ? boardPath : `${boardPath}/present`}>
              {presentation ? 'Back to app view' : 'Presentation view'}
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to={showAware('/run', { currentSlug })}>Show Dashboard</Link>
          </Button>
        </div>
      </header>
      <p role="status" className="board-update">
        {stale
          ? 'Updates interrupted — displayed information may be out of date. Retrying automatically.'
          : 'Live board · Updates every 2 seconds'}
      </p>
      <div className="board-grid">
        <section aria-labelledby="board-progress">
          <h2 id="board-progress">Judging progress</h2>
          <ReadPanel query={classReads}>
            <p className="board-metric">
              {progress.finalized} <span>of {progress.total} classes · Results in</span>
            </p>
            {!classes.length ? (
              <p>No classes configured yet.</p>
            ) : (
              <ul className="board-comb" aria-label="Class judging states">
                {classes.map(cls => {
                  const state = stateOf(cls)
                  return (
                    <li key={cls.id ?? cls.code} className={`board-hex board-hex-${state}`}>
                      <strong>{cls.code}</strong>
                      <span>{STATE_LABEL[state]}</span>
                      <span className="sr-only">{cls.displayName ?? cls.name}</span>
                    </li>
                  )
                })}
              </ul>
            )}
          </ReadPanel>
          <ul className="board-legend" aria-label="Judging state legend">
            {(Object.keys(STATE_LABEL) as ClassState[]).map(state => (
              <li key={state}>
                <span className={`board-swatch board-hex-${state}`} aria-hidden="true" />
                {STATE_LABEL[state]}
              </li>
            ))}
          </ul>
        </section>
        <div className="board-side">
          <section aria-labelledby="board-checkin">
            <h2 id="board-checkin">Checked in</h2>
            <ReadPanel query={stats}>
              {stats.data?.checkedInExhibitsCount == null ||
              stats.data?.checkedInEntrantsCount == null ? (
                <p>Check-in counts unavailable.</p>
              ) : (
                <>
                  <p className="board-metric">
                    {stats.data.checkedInExhibitsCount}
                    <span> exhibits</span>
                  </p>
                  <p>from {stats.data.checkedInEntrantsCount} entrants</p>
                </>
              )}
              <p className="board-note">
                Stored check-ins, excluding entries marked not entered. Intake waiting to sync is
                not included.
              </p>
            </ReadPanel>
          </section>
          <section aria-labelledby="board-active">
            <h2 id="board-active">Being judged now</h2>
            <ReadPanel query={classReads}>
              {!active.length ? (
                <p>No classes are being judged now.</p>
              ) : (
                <ul className="board-rows">
                  {active.map(cls => (
                    <li key={cls.id ?? cls.code}>
                      <strong>{cls.code}</strong> {cls.displayName ?? cls.name}
                    </li>
                  ))}
                </ul>
              )}
            </ReadPanel>
          </section>
        </div>
        <section aria-labelledby="board-results">
          <h2 id="board-results">Latest results in</h2>
          <p className="board-note">Finalized class results · Exhibit IDs only</p>
          <ReadPanel query={classReads}>
            {!latest.length ? (
              <p>No class results finalized yet.</p>
            ) : (
              <ul className="board-rows">
                {latest.map(cls => {
                  const recorded = cls.id ? resultClasses.get(cls.id) : undefined
                  const placements = recorded
                    ? orderPlacementColumns(recorded.placements).filter(
                        column => column.entries.length
                      )
                    : []
                  return (
                    <li key={cls.id ?? cls.code} className="board-result">
                      <div>
                        <h3>
                          {cls.code} · {cls.displayName ?? cls.name}
                        </h3>
                        <time dateTime={cls.resultsFinalizedAt!}>
                          {new Date(cls.resultsFinalizedAt!).toLocaleTimeString(undefined, {
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </time>
                      </div>
                      {cls.noAwards ? (
                        <p>No awards given</p>
                      ) : !recorded ? (
                        <p>Class results unavailable.</p>
                      ) : !placements.length ? (
                        <p>No placings recorded</p>
                      ) : (
                        <ul className="board-placings">
                          {placements.map(column => (
                            <li key={column.label}>
                              <span>{column.label}</span>{' '}
                              <strong>
                                {column.entries.map(entry => entry.exhibitId).join(', ')}
                              </strong>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </ReadPanel>
        </section>
        {show.enableClubs && (
          <div className="board-side">
            <section aria-labelledby="board-club-awards">
              <h2 id="board-club-awards">Club awards so far</h2>
              <ReadPanel query={clubs}>
                {clubs.data &&
                  clubAwards(clubs.data).map(({ title, basis, measure, award }) => (
                    <div key={title}>
                      <h3>
                        {title} <span className="board-note">· {basis}</span>
                      </h3>
                      {award.winners.length === 0 ? (
                        <p>No club has qualified yet.</p>
                      ) : (
                        <ol className="board-rows">
                          {clubAwardPlaces(award).map(([label, club], index) => (
                            <li key={`${label}-${index}`} className="board-club">
                              <span>
                                {label}: {club.clubName}
                              </span>
                              <strong>{formatClubAwardTotal(club, measure)}</strong>
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  ))}
              </ReadPanel>
            </section>
            <section aria-labelledby="board-clubs">
              <h2 id="board-clubs">Club points so far</h2>
              <ReadPanel query={clubs}>
                {clubStanding.length === 0 ? (
                  <p>No club points recorded yet.</p>
                ) : (
                  <ol className="board-rows">
                    {clubStanding.map((club, index) => (
                      <li key={`${club.clubName}-${index}`} className="board-club">
                        <span>{club.clubName}</span>
                        <strong>{club.totalPoints} pts</strong>
                      </li>
                    ))}
                  </ol>
                )}
                <p className="board-note">Live standings from recorded results.</p>
              </ReadPanel>
            </section>
          </div>
        )}
      </div>
      <footer className="board-footer">
        <ReadPanel query={release}>
          {release.data && (
            <p>
              Public results:{' '}
              {release.data.activeRelease
                ? `released${release.data.differsFromLive ? ' · live results have changed since release' : ''}`
                : 'not released'}
            </p>
          )}
        </ReadPanel>
        <Link to={showAware('/reports', { currentSlug })}>
          View public release controls in Reports
        </Link>
      </footer>
    </Container>
  )
}
